import * as React from 'react';
import { useState, useEffect } from 'react';
import { Button, Popover, Space, TreeSelect, Tag } from 'antd';
import { FilterOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useAtom } from 'jotai';
import { tagFiltersAtom, creatorTagsAtomById } from './atoms';
import { generate } from '@ant-design/colors';
import { getPaletteColor } from './utils';

const { SHOW_PARENT } = TreeSelect;

export const TagFilter: React.FC = () => {
    const [tagFilters, setTagFilters] = useAtom(tagFiltersAtom);
    const [{ data: tagOptions }] = useAtom(creatorTagsAtomById(0));
    const [visible, setVisible] = useState(false);

    // Transform and sort tag options: rename "Specific" to "Content Category" and prioritize categories
    // Kwstas request ignore this
    const filteredAndSortedTagOptions = React.useMemo(() => {
        if (!tagOptions) return [];
        
        // Rename "Specific" to "Content Category" for display
        const transformed = tagOptions.map(option => ({
            ...option,
            label: option.label === 'Specific' ? 'Content Category' : option.label
        }));
        
        // Sort to show Prime Channel Category and Content Category and Channel Type first
        const sorted = [...transformed].sort((a, b) => {
            const priorityCategories = ['Prime Channel Category', 'Content Category','Channel Type'];
            const aIndex = priorityCategories.indexOf(a.label);
            const bIndex = priorityCategories.indexOf(b.label);
            
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
            if (aIndex !== -1) return -1;
            if (bIndex !== -1) return 1;
            return 0;
        });
        
        return sorted;
    }, [tagOptions]);

    const handleChange = (newValues: string[]) => {
        setTagFilters(newValues);
    };

    const handleClearFilters = () => {
        setTagFilters([]);
        setVisible(false);
    };

    const tagRender = (props: any) => {
        const { label, value, closable, onClose } = props;

        const findColor = (options: any[], value: string): string | undefined => {
            for (const option of options) {
                if (option.value === value) return undefined;
                const found = option.children.find((child: any) => child.value === value);
                if (found) return found.color;
            }
            return undefined;
        };

        const tagColor = findColor(filteredAndSortedTagOptions, value);
        const palette = tagColor ? generate(tagColor) : [];

        return (
            <Tag
                color={tagColor}
                closable={closable}
                onClose={onClose}
                style={{ marginRight: 3, color: getPaletteColor(tagColor, palette) }}
            >
                {label}
            </Tag>
        );
    };

    const content = (
        <Space direction="vertical" style={{ width: '300px' }}>
            <TreeSelect
                treeData={filteredAndSortedTagOptions}
                value={tagFilters}
                onChange={handleChange}
                treeCheckable={true}
                showCheckedStrategy={SHOW_PARENT}
                placeholder="Select tags to filter"
                style={{ width: '100%' }}
                tagRender={tagRender}
                multiple
                allowClear
                showSearch
                filterTreeNode={(search, node) =>
                    String(node.label ?? node.title ?? '')
                        .toLowerCase()
                        .includes(search.toLowerCase())
                }
            />
            <Space style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                <Button 
                    onClick={handleClearFilters} 
                    icon={<CloseCircleOutlined />} 
                    disabled={tagFilters.length === 0}
                >
                    Clear filters
                </Button>
            </Space>
        </Space>
    );

    return (
        <Popover
            content={content}
            title="Filter by Tags"
            trigger="click"
            visible={visible}
            onVisibleChange={setVisible}
        >
            <Button 
                icon={<FilterOutlined />} 
                type={tagFilters.length > 0 ? 'primary' : 'default'}
            >
                Tags {tagFilters.length > 0 && `(${tagFilters.length})`}
            </Button>
        </Popover>
    );
};